import { useEffect, useState } from 'react'
import { Filter } from 'iconsax-react'
import { format } from 'date-fns'

import { JourneyStatusEnum } from '@/app/services/journeyService'
import { onlyCpfDigits } from '@/app/utils/cpf'
import { PartnerType } from '@/app/utils/partners.enum'
import { Button } from '@/view/components/ui/button'
import { Input } from '@/view/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/view/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/view/components/ui/select'

export type JourneySituation = 'ALL' | 'COMPLETED' | 'IN_PROGRESS' | 'ABANDONED'

export type JourneyFilterValues = {
  product: PartnerType
  situation: JourneySituation
  search: string
  from: string
  to: string
}

type JourneyFiltersProps = {
  products: PartnerType[]
  getProductLabel: (key?: string | null) => string
  value: JourneyFilterValues
  onApply: (value: JourneyFilterValues) => void
}

const toInputDate = (iso: string) => (iso ? iso.slice(0, 10) : '')

const startOfDayIso = (date: string) =>
  date ? new Date(`${date}T00:00:00`).toISOString() : ''

const endOfDayIso = (date: string) =>
  date ? new Date(`${date}T23:59:59`).toISOString() : ''

const situationLabels: Record<JourneySituation, string> = {
  ALL: 'Todas',
  [JourneyStatusEnum.COMPLETED]: 'Concluídas',
  [JourneyStatusEnum.IN_PROGRESS]: 'Em andamento',
  ABANDONED: 'Abandonadas',
}

const summarize = (value: JourneyFilterValues, productLabel: string) => {
  const parts = [productLabel]

  if (value.situation !== 'ALL') parts.push(situationLabels[value.situation])
  if (value.search) parts.push(value.search)

  if (value.from && value.to) {
    parts.push(
      `${format(new Date(value.from), 'dd/MM/yy')} - ${format(
        new Date(value.to),
        'dd/MM/yy',
      )}`,
    )
  }

  return parts.join(' · ')
}

export const JourneyFilters = ({
  products,
  getProductLabel,
  value,
  onApply,
}: JourneyFiltersProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [draft, setDraft] = useState<JourneyFilterValues>(value)

  useEffect(() => {
    if (!isOpen) setDraft(value)
  }, [isOpen, value])

  const setField = <K extends keyof JourneyFilterValues>(
    field: K,
    fieldValue: JourneyFilterValues[K],
  ) => setDraft((current) => ({ ...current, [field]: fieldValue }))

  const handleApply = () => {
    onApply({ ...draft, search: onlyCpfDigits(draft.search) })
    setIsOpen(false)
  }

  const handleClear = () => {
    const cleared: JourneyFilterValues = {
      ...draft,
      situation: 'ALL',
      search: '',
    }

    setDraft(cleared)
    onApply(cleared)
    setIsOpen(false)
  }

  return (
    <div className="flex items-center justify-between">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <div className="flex cursor-pointer items-center gap-2 rounded-lg border-[1px] border-gray01 px-3 py-2">
            <Filter size="20" />
            <p className="text-xs font-semibold">
              {summarize(value, getProductLabel(value.product))}
            </p>
          </div>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[560px] max-w-[90vw] p-0">
          <div className="m-4 flex flex-wrap gap-4">
            <div className="min-w-[calc(50%-0.5rem)] flex-1 max-md:min-w-full">
              <label className="text-xs font-semibold">Produto</label>
              <Select
                value={draft.product}
                onValueChange={(next) =>
                  setField('product', next as PartnerType)
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {products.map((item) => (
                    <SelectItem key={item} value={item}>
                      {getProductLabel(item)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[calc(50%-0.5rem)] flex-1 max-md:min-w-full">
              <label className="text-xs font-semibold">Situação</label>
              <Select
                value={draft.situation}
                onValueChange={(next) =>
                  setField('situation', next as JourneySituation)
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(situationLabels) as JourneySituation[]).map(
                    (situation) => (
                      <SelectItem key={situation} value={situation}>
                        {situationLabels[situation]}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[calc(50%-0.5rem)] flex-1 max-md:min-w-full">
              <label className="text-xs font-semibold">De</label>
              <Input
                type="date"
                className="mt-1"
                value={toInputDate(draft.from)}
                onChange={(event) =>
                  setField('from', startOfDayIso(event.target.value))
                }
              />
            </div>

            <div className="min-w-[calc(50%-0.5rem)] flex-1 max-md:min-w-full">
              <label className="text-xs font-semibold">Até</label>
              <Input
                type="date"
                className="mt-1"
                value={toInputDate(draft.to)}
                onChange={(event) =>
                  setField('to', endOfDayIso(event.target.value))
                }
              />
            </div>

            <div className="min-w-full">
              <label className="text-xs font-semibold">
                CPF do cliente ou vendedor
              </label>
              <Input
                className="mt-1"
                value={draft.search}
                onChange={(event) =>
                  setField('search', onlyCpfDigits(event.target.value))
                }
              />
            </div>
          </div>

          <div className="my-5 w-full border-b-[1px] border-gray01" />

          <div className="flex items-center justify-between p-4">
            <Button
              type="button"
              onClick={handleClear}
              variant="outline"
              className="w-24 rounded-full bg-gray02 text-xs font-bold"
            >
              Limpar
            </Button>
            <Button
              type="button"
              onClick={handleApply}
              className="w-20 rounded-full bg-primary text-xs font-bold text-white01"
            >
              Filtrar
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
